import { createI18n } from 'vue-i18n'
import { useCookie } from '@core/composable/useCookie'
import { themeConfig } from '@themeConfig'

const messages = Object.fromEntries(Object.entries(import.meta.glob('./locales/*.json', { eager: true }))
  .map(([key, value]) => [key.slice(10, -5), value.default]))

// A single persistent ref backing the `language` cookie — read once at i18n init
// and written to whenever `setLocale` runs, so the chosen language survives a reload.
// Uses `@core/composable/useCookie` (not `@layouts/stores/config`'s `cookieRef`) to
// avoid pulling the whole layout-store dependency graph into every i18n consumer.
const languageCookie = useCookie(`${themeConfig.app.title}-language`, {
  default: () => themeConfig.app.i18n.defaultLocale,
})

// Ignore stale cookies for languages no longer in product scope (e.g. leftover `en`
// after Dutch-only). `en.json` remains loaded only as fallbackLocale for missing keys.
const resolveInitialLocale = () => {
  const supported = themeConfig.app.i18n.langConfig?.map(entry => entry.i18nLang) || []
  const fromCookie = languageCookie.value

  if (supported.includes(fromCookie) && messages[fromCookie])
    return fromCookie

  const fallback = themeConfig.app.i18n.defaultLocale

  languageCookie.value = fallback
  
  return fallback
}

let _i18n = null
export const getI18n = () => {
  if (_i18n === null) {
    _i18n = createI18n({
      legacy: false,
      locale: resolveInitialLocale(),

      // Safety net for missing keys only — not a user-facing language
      // (`design-system.md` §2.6: Dutch is the only product UI language).
      fallbackLocale: 'en',
      messages,
    })
  }
  
  return _i18n
}

/**
 * Sets the active UI language and persists it so it survives a reload.
 * Silently no-ops for a locale that isn't registered in `themeConfig`'s
 * `langConfig` or has no message catalog — a locale must never be applied
 * with no visible effect (`design-system.md` §2.6, §10).
 */
export const setLocale = lang => {
  const isSupported = themeConfig.app.i18n.langConfig?.some(entry => entry.i18nLang === lang)
  if (!isSupported || !messages[lang])
    return

  getI18n().global.locale.value = lang
  languageCookie.value = lang
}

export default function (app) {
  app.use(getI18n())
}
